import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';

interface Post {
  id: number;
  title: string;
  slug: string;
  category: string;
  created_at: string;
  cover_image_url?: string;
}

interface RelatedPostsProps {
  currentPostId: number;
  category: string;
}

export function RelatedPosts({ currentPostId, category }: RelatedPostsProps) {
  const [posts, setPosts] = useState<Post[]>([]);

  useEffect(() => {
    loadRelatedPosts();
  }, [currentPostId, category]);

  const loadRelatedPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, slug, category, created_at, cover_image_url')
        .eq('category', category)
        .neq('id', currentPostId)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Erro ao carregar posts relacionados:', error);
    }
  };

  if (posts.length === 0) return null;

  return (
    <section className="mt-16 pt-8 border-t border-kopf-secondary">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles className="w-5 h-5 text-kopf-accent" />
        <h2 className="text-xl sm:text-2xl font-bold text-kopf-primary">Você Também Pode Gostar</h2>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {posts.map((post) => (
          <Link key={post.id} to={`/post/${post.slug}`}>
            <Card className="group overflow-hidden border-kopf-secondary hover:border-kopf-accent transition-all duration-300 hover:shadow-lg h-full">
              <div className="relative h-32 sm:h-40 overflow-hidden">
                {post.cover_image_url ? (
                  <img 
                    src={post.cover_image_url} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-kopf-primary to-kopf-accent" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-kopf-text/40 to-transparent" />
                <Badge className="absolute top-2 right-2 bg-kopf-primary text-white border-0 text-xs">
                  {post.category}
                </Badge>
              </div>
              
              <CardContent className="p-4">
                <h3 className="font-semibold text-kopf-primary mb-2 line-clamp-2 group-hover:text-kopf-accent transition-colors">
                  {post.title}
                </h3>
                
                <div className="flex items-center gap-1 text-xs text-kopf-text/60">
                  <Calendar className="w-3 h-3" />
                  <span>{format(new Date(post.created_at), 'dd MMM yyyy', { locale: ptBR })}</span>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
